/**
 * Shared PDF delivery utilities.
 *
 * Consolidates the repeated base64-decode, print, and download logic that was
 * previously duplicated across configThunks and DataTable.
 */

/**
 * Decode the raw API response from /pabreport/pdf into a Blob.
 * The API returns { success, data: "<base64 string>", filename } or, rarely, a raw Blob.
 */
export function decodePdfResponseToBlob(response: unknown): Blob {
  if (response instanceof Blob) {
    return response;
  }

  if (response && typeof response === "object") {
    const r = response as Record<string, unknown>;
    const base64Data = r.data ?? r.content ?? r.pdf;

    if (typeof base64Data === "string" && base64Data.length > 0) {
      const byteCharacters = atob(base64Data);
      const byteArray = new Uint8Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteArray[i] = byteCharacters.charCodeAt(i);
      }
      return new Blob([byteArray], { type: "application/pdf" });
    }

    throw new Error("No PDF data found in response");
  }

  throw new Error("Unexpected response format from PDF endpoint");
}

/**
 * Build a consistent download filename: `{reportName}_{idFallback}.pdf`
 */
export function buildPdfFileName(reportName: string, idFallback: string): string {
  const safe = (idFallback || "document").toString().replace(/[/\\?%*:|"<>]/g, "_");
  return `${reportName}_${safe}.pdf`;
}

/**
 * Open the OS print dialog by loading the PDF blob into a hidden iframe.
 * The iframe is removed and the object URL revoked after 60s to allow the
 * print dialog to remain open while still printing.
 */
export function printPdfBlob(blob: Blob): void {
  const url = URL.createObjectURL(blob);

  const iframe = document.createElement("iframe");
  iframe.style.cssText =
    "position:fixed;right:0;bottom:0;width:0;height:0;border:none;visibility:hidden;";
  iframe.src = url;

  iframe.onload = () => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } catch {
      // If print fails for any reason, clean up immediately
      URL.revokeObjectURL(url);
      iframe.remove();
      return;
    }
    // Revoke after a long delay so the dialog + spooler can finish
    setTimeout(() => {
      URL.revokeObjectURL(url);
      iframe.remove();
    }, 60_000);
  };

  document.body.appendChild(iframe);
}

/**
 * Trigger a browser download for the PDF blob.
 * Does NOT open a new tab.
 */
export function downloadPdfBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Revoke shortly after to allow the download to start
  setTimeout(() => URL.revokeObjectURL(url), 5_000);
}
